import { useCallback, useMemo, useState } from "react";
import { getTabLabel } from "@demandproai/shared";

const VIEW_MODES = {
  DOCUMENT: "document",
  TABS: "tabs",
};

// groups sections by type and manages the active tab for the VS Code-style view
const useDemandStudioTabView = ({ sectionDefinitions = [], sectionComments = [], initialTabType = null }) => {
  const [viewMode, setViewMode] = useState(VIEW_MODES.TABS);
  const [activeTabType, setActiveTabType] = useState(initialTabType);

  const isTabView = viewMode === VIEW_MODES.TABS;

  const tabs = useMemo(() => {
    const seen = new Set();
    return sectionDefinitions.reduce((acc, section) => {
      if (seen.has(section.type)) return acc;
      seen.add(section.type);
      acc.push({ type: section.type, label: getTabLabel(section) });
      return acc;
    }, []);
  }, [sectionDefinitions]);

  const resolvedActiveTabType = useMemo(() => {
    if (activeTabType && tabs.some((t) => t.type === activeTabType)) return activeTabType;
    return tabs[0]?.type || null;
  }, [activeTabType, tabs]);

  const toggleViewMode = useCallback(() => {
    setViewMode((prev) =>
      prev === VIEW_MODES.DOCUMENT ? VIEW_MODES.TABS : VIEW_MODES.DOCUMENT
    );
  }, []);

  const handleTabClick = useCallback((type) => {
    setActiveTabType(type);
  }, []);

  const getTabTypeForSection = useCallback((sectionId) => {
    const section = sectionDefinitions.find((s) => s.id === sectionId);
    return section?.type || null;
  }, [sectionDefinitions]);

  const visibleSectionComments = useMemo(() => {
    if (!isTabView || !resolvedActiveTabType) return sectionComments;
    return sectionComments.filter((s) => sectionDefinitions.find((sd) => sd.id === s.id)?.type === resolvedActiveTabType);
  }, [isTabView, resolvedActiveTabType, sectionComments, sectionDefinitions]);

  return {
    viewMode,
    isTabView,
    activeTabType: resolvedActiveTabType,
    tabs,
    visibleSectionComments,
    toggleViewMode,
    handleTabClick,
    getTabTypeForSection,
  };
};

export { VIEW_MODES };
export default useDemandStudioTabView;
